import * as React from 'react';
import { useRef } from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import type { VariantProps } from 'class-variance-authority';
import { useScroll } from 'ahooks';
import { cn } from '../../utils/cn';
import type { _scrollBarVariant } from './scroll-bar';
import { ScrollBar } from './scroll-bar';

interface ScrollAreaProps extends VariantProps<typeof _scrollBarVariant> {
    scrollBarClassName?: string;
    thumbClassName?: string;
    /**
     * The overscroll behavior of the scroll area.
     */
    overscroll?: 'contain' | 'none';
    /**
     * Callback when the scroll area is scrolled.
     * @param val The current scroll position.
     */
    onCustomScroll?: (val: {
        left: number;
        right: number;
        top: number;
        bottom: number;
    }) => void;
}

export const ScrollArea = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> &
        ScrollAreaProps
>(
    (
        {
            className,
            scrollBarClassName,
            thumbClassName,
            overscroll,
            children,
            scrollBarVisibility,
            onCustomScroll,
            ...props
        },
        ref,
    ) => {
        const scrollRef = useRef<HTMLDivElement>();
        useScroll(scrollRef, (val) => {
            if (onCustomScroll && scrollRef?.current && val) {
                onCustomScroll({
                    left: val.left,
                    right:
                        scrollRef.current.scrollWidth -
                        val.left -
                        scrollRef.current.clientWidth,
                    top: val.top,
                    bottom:
                        scrollRef.current.scrollHeight -
                        val.top -
                        scrollRef.current.clientHeight,
                });
            }
            return true;
        });

        return (
            <ScrollAreaPrimitive.Root
                data-slot="scroll-area"
                className={cn('relative', className)}
                {...props}
            >
                <ScrollAreaPrimitive.Viewport
                    ref={(element) => {
                        // Assign both ref variables to this element.
                        scrollRef.current = element ?? undefined;
                        if (typeof ref === 'function') {
                            ref(element);
                        } else if (ref) {
                            ref.current = element;
                        }
                    }}
                    data-slot="scroll-area-viewport"
                    className={cn(
                        'focus-visible:ring-ring/50 outline-hidden size-full rounded-[inherit] transition-[color,box-shadow] focus-visible:outline-1 focus-visible:ring-[3px]',
                        overscroll === 'contain' && 'overscroll-contain',
                        overscroll === 'none' && 'overscroll-none',
                    )}
                >
                    {children}
                </ScrollAreaPrimitive.Viewport>
                <ScrollBar
                    className={scrollBarClassName}
                    thumbClassName={thumbClassName}
                    scrollBarVisibility={scrollBarVisibility}
                />
                <ScrollAreaPrimitive.Corner />
            </ScrollAreaPrimitive.Root>
        );
    },
);

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
